import * as React from "react";

import { Button, type ButtonProps } from "./button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

export interface TooltipButtonProps extends Omit<ButtonProps, "aria-label"> {
  /**
   * Tooltip text AND aria-label — single source of truth so the two never
   * drift. The label is read by screen readers (a11y) and surfaced as a
   * visible tooltip on hover (sighted discoverability).
   */
  label: string;
  /** Tooltip placement. Default "bottom". */
  side?: "top" | "bottom" | "left" | "right";
}

/**
 * SIM-1617: icon-button plus tooltip; one `label` prop feeds both `aria-label` and the visible
 * tooltip, so they cannot drift. Use it for every toolbar icon button instead of stacking your own
 * Tooltip chain. A local `<TooltipProvider>` is mounted inside so it works standalone in test
 * renders — Radix allows nested providers and the nearest ancestor's delay wins.
 */
const TooltipButton = React.forwardRef<HTMLButtonElement, TooltipButtonProps>(
  ({ label, side = "bottom", children, ...rest }, ref) => (
    <TooltipProvider delayDuration={700}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button ref={ref} aria-label={label} {...rest}>
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side={side}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ),
);
TooltipButton.displayName = "TooltipButton";

export { TooltipButton };
